<template>
  <div class="px-5 sm:px-6 lg:px-10">

    <div v-if="!can.view" class="mt-10"></div>

    <template v-else>
      <template v-if="!showForm">
        <div class="flex items-center justify-between mb-12 mt-6">
          <h1 class="text-[22px] sm:text-[24px] leading-7 font-bold text-gray-900">My Suppliers</h1>

          <div class="flex items-center gap-3">
            <button
              type="button"
              class="inline-flex items-center gap-2 h-12 px-12 text-gray-900 font-bold text-xl hover:bg-gray-50"
              @click="goServices"
            >
              <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <rect x="2" y="2" width="6" height="6" rx="1" />
                <rect x="12" y="2" width="6" height="6" rx="1" />
                <rect x="2" y="12" width="6" height="6" rx="1" />
                <rect x="12" y="12" width="6" height="6" rx="1" />
              </svg>
              <span>Services</span>
            </button>

            <button
              type="button"
              class="inline-flex items-center gap-2 h-9 px-4 rounded-full font-bold text-white text-sm shadow-sm hover:opacity-95"
              :style="{ backgroundColor: '#5B9AC5' }"
              @click="openAdd"
            >
              + New Supplier
            </button>
          </div>
        </div>

        <div v-if="loadingList" class="flex justify-center py-16">
          <svg class="animate-spin h-8 w-8 text-[#5B9AC5]" viewBox="0 0 24 24" fill="none">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
          </svg>
        </div>

        <div v-else-if="listError" class="text-center py-16 text-red-500 text-sm">
          {{ listError }}
          <button class="ml-3 underline text-[#5B9AC5]" @click="fetchSuppliers">Retry</button>
        </div>

        <DynamicTable
          v-else
          :rows="rows"
          :columns="equalColumns"
          :rowsPerPage="50"
          :rowsPerPageOptions="[50]"
          :showHeader="false"
          :showAddButton="false"
        >
          <template #cell:phone="{ row }">
            <span>{{ row.countryCode }} {{ row.phone }}</span>
          </template>

          <template #cell:services="{ value, row }">
            <div>
              <span>
                {{
                  (expandedRows[row.id]
                    ? (value || [])
                    : (value || []).slice(0, 2)
                  ).join(', ')
                }}
              </span>
              <span
                v-if="(value || []).length > 2"
                class="text-[#059afd] cursor-pointer ml-1"
                @click="toggleExpand(row.id)"
              >
                {{ expandedRows[row.id] ? ' less' : '...' }}
              </span>
            </div>
          </template>

          <template #action="{ row }">
            <div class="flex items-center gap-3 justify-center text-[13px]">
              <button class="flex items-center gap-1 text-gray-800" @click="openView(row)">
                <i class="fas fa-eye"></i><span>View</span>
              </button>
              <button class="flex items-center gap-1 text-gray-800" @click="openEdit(row)">
                <svg viewBox="0 0 24 24" class="w-4 h-4"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm18-11.5a1 1 0 0 0 0-1.41L18.66 3a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75L21 5.75Z" fill="currentColor"/></svg>
                <span>Edit</span>
              </button>
              <button class="flex items-center gap-1 text-red-500" @click="confirmDelete(row)">
                <svg viewBox="0 0 24 24" class="w-4 h-4" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12ZM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4Z"/></svg>
                <span>Delete</span>
              </button>
            </div>
          </template>
        </DynamicTable>
      </template>

      <template v-else>
        <div class="mb-8">
          <div class="pb-2 border-gray-300">
            <h1 class="text-[22px] sm:text-[24px] leading-7 pb-2 font-bold text-gray-900">
              {{ pageTitle }}
            </h1>

            <div v-if="formMode !== 'view'" class="mt-4 flex justify-end">
              <button
                type="button"
                class="h-8 px-3 rounded-full text-sm font-bold text-white shadow-sm hover:opacity-95"
                :style="{ backgroundColor: '#5B9AC5' }"
                :disabled="submitting"
                @click="submit"
              >
                <span v-if="submitting">Saving…</span>
                <span v-else>{{ formMode === 'add' ? 'Add Supplier' : 'Save' }}</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="formError" class="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
          {{ formError }}
        </div>

        <div class="border-2 border-gray-300 rounded-2xl shadow-sm">
          <div class="max-w-[900px] mx-auto">
            <div class="bg-white rounded-2xl p-6 sm:p-8">
              <DynamicForm
                :title="''"
                :schema="formSchema"
                :mode="formMode === 'view' ? 'view' : 'edit'"
                v-model="form"
                :showActions="false"
                wrapperClass="p-0 bg-transparent"
                rowClass="text-lg"
              >
                <!-- Supplier country code searchable dropdown -->
                <template #field:country_code_select>
                  <div class="relative" ref="countryWrapper">
                    <label class="block text-sm font-bold text-gray-700 mb-1">Country Code</label>
                    <button
                      type="button"
                      class="w-full h-10 px-3 rounded-xl border-2 border-gray-300 text-left text-sm bg-white flex items-center gap-2"
                      :class="{ 'opacity-60 pointer-events-none': formMode === 'view' }"
                      @click="formMode !== 'view' && (showCountryDD = !showCountryDD)"
                    >
                      <img v-if="selectedCountry" :src="selectedCountry.flag" class="w-5 h-4 object-cover rounded-sm" alt="" @error="e => (e.target.style.visibility = 'hidden')" />
                      <span v-if="selectedCountry" class="text-gray-900">{{ selectedCountry.dial }}</span>
                      <span v-else class="text-gray-400">Select country code</span>
                    </button>

                    <div
                      v-show="showCountryDD"
                      class="absolute z-50 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg"
                    >
                      <div class="p-2 border-b border-gray-100">
                        <input
                          v-model="countrySearch"
                          type="text"
                          placeholder="Search by name or code..."
                          class="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm"
                          @click.stop
                        />
                      </div>
                      <div class="max-h-60 overflow-auto py-1">
                        <div v-if="countriesLoading" class="px-3 py-2 text-sm text-gray-400">Loading…</div>
                        <template v-else>
                          <button
                            v-for="c in filteredCountries"
                            :key="c.code"
                            type="button"
                            class="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 text-sm"
                            @click="selectCountry(c)"
                          >
                            <img :src="c.flag" class="w-5 h-4 object-cover rounded-sm" alt="" @error="e => (e.target.style.visibility = 'hidden')" />
                            <span class="text-gray-800 flex-1 truncate">{{ c.name }}</span>
                            <span class="text-gray-500">{{ c.dial }}</span>
                          </button>
                          <div v-if="!filteredCountries.length" class="px-3 py-2 text-sm text-gray-400">No results</div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>

                <template #field:servicesSelect>
                  <div class="relative" ref="servicesWrapper">
                    <label class="block text-sm font-bold text-gray-700 mb-1">Services</label>
                    <button
                      type="button"
                      class="w-full h-10 px-3 rounded-xl border-2 border-gray-300 text-left text-sm bg-white"
                      :class="{ 'opacity-60 pointer-events-none': formMode === 'view' }"
                      @click="formMode !== 'view' && (showServicesDD = !showServicesDD)"
                    >
                      <span v-if="selectedServiceObjects.length === 0" class="text-gray-400">Select service</span>
                      <span v-else class="text-gray-900">
                        {{ selectedServiceObjects.map(s => s.label).join(', ') }}
                      </span>
                    </button>

                    <div
                      v-show="showServicesDD"
                      ref="servicesMenu"
                      class="absolute z-50 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg"
                    >
                      <div class="max-h-60 overflow-auto py-2">
                        <label
                          v-for="opt in serviceOptions"
                          :key="`svc-${opt.value}`"
                          class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            class="h-4 w-4"
                            :checked="form.services.includes(opt.value)"
                            @change="toggleService(opt.value, $event.target.checked)"
                          />
                          <span class="text-sm text-gray-800">{{ opt.label }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </template>

                <template #field:selectedServicesPills>
                  <div class="flex flex-wrap gap-2 pt-1">
                    <span
                      v-for="(s, i) in selectedServiceObjects"
                      :key="`pill-${s.value}-${i}`"
                      class="px-4 h-9 inline-flex items-center rounded-xl border-[2px] border-gray-300 text-gray-900 text-sm font-semibold"
                    >
                      {{ s.label || 'Service Name' }}
                      <button
                        v-if="formMode !== 'view'"
                        type="button"
                        class="ml-2 text-gray-500 hover:text-gray-700"
                        @click="removeService(s.value)"
                        aria-label="remove"
                      >✕</button>
                    </span>
                  </div>
                </template>

                <!-- Representative country code searchable dropdown -->
                <template #field:representative_country_code_select>
                  <div class="relative" ref="repCountryWrapper">
                    <label class="block text-sm font-bold text-gray-700 mb-1">Country Code</label>
                    <button
                      type="button"
                      class="w-full h-10 px-3 rounded-xl border-2 border-gray-300 text-left text-sm bg-white flex items-center gap-2"
                      :class="{ 'opacity-60 pointer-events-none': formMode === 'view' }"
                      @click="formMode !== 'view' && (showRepCountryDD = !showRepCountryDD)"
                    >
                      <img v-if="selectedRepCountry" :src="selectedRepCountry.flag" class="w-5 h-4 object-cover rounded-sm" alt="" @error="e => (e.target.style.visibility = 'hidden')" />
                      <span v-if="selectedRepCountry" class="text-gray-900">{{ selectedRepCountry.dial }}</span>
                      <span v-else class="text-gray-400">Select country code</span>
                    </button>

                    <div
                      v-show="showRepCountryDD"
                      class="absolute z-50 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg"
                    >
                      <div class="p-2 border-b border-gray-100">
                        <input
                          v-model="repCountrySearch"
                          type="text"
                          placeholder="Search by name or code..."
                          class="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm"
                          @click.stop
                        />
                      </div>
                      <div class="max-h-60 overflow-auto py-1">
                        <div v-if="countriesLoading" class="px-3 py-2 text-sm text-gray-400">Loading…</div>
                        <template v-else>
                          <button
                            v-for="c in filteredRepCountries"
                            :key="c.code"
                            type="button"
                            class="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 text-sm"
                            @click="selectRepCountry(c)"
                          >
                            <img :src="c.flag" class="w-5 h-4 object-cover rounded-sm" alt="" @error="e => (e.target.style.visibility = 'hidden')" />
                            <span class="text-gray-800 flex-1 truncate">{{ c.name }}</span>
                            <span class="text-gray-500">{{ c.dial }}</span>
                          </button>
                          <div v-if="!filteredRepCountries.length" class="px-3 py-2 text-sm text-gray-400">No results</div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
              </DynamicForm>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import DynamicTable from "@/components/DynamicTable.vue";
import DynamicForm  from "@/components/DynamicForm.vue";
import api          from "@/api.js";
import Swal         from "sweetalert2";
import { loadMyPermissions, hasAny } from "@/utils/permissions";

export default {
  name: "Suppliers",
  components: { DynamicTable, DynamicForm },

  data() {
    return {
      can: {
        view: false, // others.suppliers
      },

      rows:         [],
      loadingList:  false,
      listError:    null,
      expandedRows: {},
      showForm:     false,
      formMode:     "add",
      submitting:   false,
      formError:    null,
      editingId:    null,
      form:         this.emptyForm(),

      serviceOptions: [],
      showServicesDD: false,

      // Country codes (searchable, fetched from API)
      countries:         [],
      countriesLoading:  false,
      showCountryDD:      false,
      countrySearch:      "",
      showRepCountryDD:   false,
      repCountrySearch:   "",
    };
  },

  computed: {
    equalColumns() {
      return [
        { key: "id",           label: "ID",            width: "80px"  },
        { key: "supplierName", label: "Supplier Name", width: "260px" },
        { key: "phone",        label: "Phone Number",  width: "170px" },
        { key: "services",     label: "Services",      width: "300px" },
        { key: "action",       label: "Action",        width: "160px" },
      ];
    },

    pageTitle() {
      if (this.formMode === "edit") return "Edit Supplier";
      if (this.formMode === "view") return "View Supplier";
      return "Add Supplier";
    },

    formSchema() {
      return {
        sections: [
          {
            title: "Supplier Info",
            cols: 2,
            fields: [
              { key: "supplier_name",         label: "Supplier Name", type: "text", placeholder: "Enter supplier name", colSpan: 2 },
              { key: "country_code_select",   slot: true },
              { key: "phone_number",          label: "Phone Number",  type: "text", placeholder: "Enter phone number" },
              { key: "email",                 label: "Email",         type: "text", placeholder: "Enter Email" },
              { key: "tax_no",                label: "tax no.",       type: "text", placeholder: "Enter Tax no." },
              { key: "servicesSelect",        slot: true, colSpan: 2 },
              { key: "selectedServicesPills", slot: true, colSpan: 2 },
            ],
          },
          {
            title: "Representative Info",
            cols: 2,
            fields: [
              { key: "representative_name",                label: "Representative Name", type: "text",     placeholder: "Enter representative name", colSpan: 2 },
              { key: "representative_country_code_select", slot: true },
              { key: "representative_phone",               label: "Phone Number",        type: "text",     placeholder: "Enter phone number" },
              { key: "note",                                label: "Note",                type: "textarea", placeholder: "Enter note", colSpan: 2 },
            ],
          },
        ],
      };
    },

    selectedServiceObjects() {
      return (this.form.services || [])
        .map(v => this.serviceOptions.find(o => o.value === v))
        .filter(Boolean);
    },

    filteredCountries() {
      const q = (this.countrySearch || "").trim().toLowerCase();
      if (!q) return this.countries;
      return this.countries.filter(
        c => c.name.toLowerCase().includes(q) || c.dial.includes(q)
      );
    },

    filteredRepCountries() {
      const q = (this.repCountrySearch || "").trim().toLowerCase();
      if (!q) return this.countries;
      return this.countries.filter(
        c => c.name.toLowerCase().includes(q) || c.dial.includes(q)
      );
    },

    selectedCountry() {
      return this.countries.find(c => c.dial === this.form.country_code) || null;
    },

    selectedRepCountry() {
      return this.countries.find(c => c.dial === this.form.representative_country_code) || null;
    },
  },

  async created() {
    await this.ensurePermissions();
    if (!this.can.view) return;
    this.fetchSuppliers();
    this.fetchServiceOptions();
    this.fetchCountries();
  },

  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },

  watch: {
    // Closing the form now goes through the router's own history (query param)
    // instead of raw history.pushState/back, which conflicts with Vue Router's
    // internal history handling and was causing the replaceState/CORS-looking error.
    "$route.query.supplierForm"(val) {
      if (!val) this.showForm = false;
    },
  },

  methods: {
    async ensurePermissions() {
      try {
        await loadMyPermissions(false);
        this.can.view = hasAny(["others.suppliers"]);
      } catch (_) {
        this.can.view = false;
      }
    },

    emptyForm() {
      return {
        supplier_name:                "",
        country_code:                 "",
        phone_number:                 "",
        email:                        "",
        tax_no:                       "",
        services:                     [],
        servicesSelect:               "",
        representative_name:          "",
        representative_country_code:  "",
        representative_phone:         "",
        note:                         "",
        selectedServicesPills:        "",
      };
    },

    toggleExpand(id) {
      this.expandedRows = { ...this.expandedRows, [id]: !this.expandedRows[id] };
    },

    mapToRow(s) {
      return {
        id:           s.id,
        supplierName: s.supplier_name || "",
        countryCode:  s.country_code  || "",
        phone:        s.phone_number  || "",
        services: Array.isArray(s.services)
          ? s.services.map(sv => (typeof sv === "object" ? sv.name : sv))
          : [],
        _raw: s,
      };
    },

    async fetchSuppliers() {
      this.loadingList = true;
      this.listError   = null;
      try {
        const res  = await api.get("/accounting/suppliers");
        const data = res.data?.data ?? res.data ?? [];
        const sorted = (Array.isArray(data) ? data : []).sort(
          (a, b) => Number(b.id) - Number(a.id)
        );
        this.rows  = sorted.map(this.mapToRow);
      } catch (err) {
        this.listError = err?.response?.data?.message || "Failed to load suppliers.";
      } finally {
        this.loadingList = false;
      }
    },

    async fetchServiceOptions() {
      try {
        const res  = await api.get("/accounting/supplier-services");
        const data = res.data?.data ?? res.data ?? [];
        this.serviceOptions = (Array.isArray(data) ? data : []).map(s => ({
          value: s.id,
          label: s.name || s.service_name || "",
        }));
      } catch {
        // non-fatal
      }
    },

    // Fetch countries (name + flag + dial code) once, cache in sessionStorage
    async fetchCountries() {
      const cacheKey = "lyon_countries_cache_v2";
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          this.countries = JSON.parse(cached);
          return;
        }
      } catch (_) {}

      this.countriesLoading = true;
      try {
        const res  = await fetch("https://cdn.jsdelivr.net/npm/world-countries@4/countries.json");
        const data = await res.json();

        this.countries = (Array.isArray(data) ? data : [])
          .map(c => {
            const root   = c.idd?.root || "";
            const suffix = (c.idd?.suffixes && c.idd.suffixes.length === 1) ? c.idd.suffixes[0] : "";
            const dial   = root ? `${root}${suffix}` : "";
            const iso2   = (c.cca2 || "").toLowerCase();
            return {
              code: c.cca2,
              name: c.name?.common || "",
              flag: iso2 ? `https://flagcdn.com/${iso2}.svg` : "",
              dial,
            };
          })
          .filter(c => c.dial && c.name && c.flag)
          .sort((a, b) => a.name.localeCompare(b.name));

        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(this.countries));
        } catch (_) {}
      } catch (err) {
        this.countries = [];
      } finally {
        this.countriesLoading = false;
      }
    },

    selectCountry(c) {
      this.form.country_code = c.dial;
      this.showCountryDD     = false;
      this.countrySearch     = "";
    },

    selectRepCountry(c) {
      this.form.representative_country_code = c.dial;
      this.showRepCountryDD                 = false;
      this.repCountrySearch                 = "";
    },

    goServices() { this.$router.push({ name: "SuppliersServices" }).catch(() => {}); },

    openForm() {
      this.$router
        .push({ query: { ...this.$route.query, supplierForm: "1" } })
        .catch(() => {});
    },

    closeForm() {
      if (this.$route.query.supplierForm) {
        this.$router.back();
      } else {
        this.showForm = false;
      }
    },

    openAdd() {
      if (!this.can.view) return;
      this.formMode  = "add";
      this.editingId = null;
      this.formError = null;
      this.form      = this.emptyForm();
      this.showForm  = true;
      this.openForm();
    },

    openView(row) {
      if (!this.can.view) return;
      this.fillFromRow(row);
      this.formMode  = "view";
      this.formError = null;
      this.showForm  = true;
      this.openForm();
    },

    openEdit(row) {
      if (!this.can.view) return;
      this.fillFromRow(row);
      this.formMode  = "edit";
      this.formError = null;
      this.showForm  = true;
      this.openForm();
    },

    fillFromRow(row) {
      const s = row._raw || {};
      this.editingId = row.id;

      let serviceIds = [];
      if (Array.isArray(s.services)) {
        serviceIds = s.services
          .map(sv => (typeof sv === "object" ? (sv.service_id ?? sv.id) : this.serviceOptions.find(o => o.label === sv)?.value))
          .filter(v => v != null);
      }

      this.form = {
        supplier_name:                s.supplier_name                || "",
        country_code:                 s.country_code                 || "",
        phone_number:                 s.phone_number                 || "",
        email:                        s.email                        || "",
        tax_no:                       s.tax_no                       || "",
        services:                     serviceIds,
        servicesSelect:               "",
        representative_name:          s.representative_name          || "",
        representative_country_code:  s.representative_country_code  || "",
        representative_phone:         s.representative_phone         || "",
        note:                         s.note                         || "",
        selectedServicesPills:        "",
      };
    },

    async submit() {
      if (this.formMode === "view" || !this.can.view) return;
      this.submitting = true;
      this.formError  = null;

      const payload = {
        supplier_name:                this.form.supplier_name,
        country_code:                 this.form.country_code,
        phone_number:                 this.form.phone_number,
        email:                        this.form.email,
        tax_no:                       this.form.tax_no,
        representative_name:          this.form.representative_name,
        representative_country_code:  this.form.representative_country_code,
        representative_phone:         this.form.representative_phone,
        note:                         this.form.note,
        service_ids:                  this.form.services,
      };

      try {
        if (this.formMode === "edit") {
          await api.put(`/accounting/suppliers/${this.editingId}`, payload);
        } else {
          await api.post("/accounting/suppliers", payload);
        }
        await this.fetchSuppliers();
        this.closeForm();
        await Swal.fire({ icon: 'success', title: this.formMode === 'add' ? 'Supplier added successfully' : 'Supplier updated successfully', toast: true, position: 'top', timer: 1400, showConfirmButton: false });
      } catch (err) {
        this.formError = err?.response?.data?.message || "An error occurred. Please try again.";
        await Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || err?.message || 'Failed to save supplier.', toast: true, position: 'top', timer: 2200, showConfirmButton: false });
      } finally {
        this.submitting = false;
      }
    },

    async confirmDelete(row) {
      if (!this.can.view) return;
      const result = await Swal.fire({
        title: 'Delete Supplier?',
        text: `"${row.supplierName}" will be permanently deleted.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        buttonsStyling: false,
        customClass: { confirmButton: 'swal2-confirm-btn-fixed', cancelButton: 'swal2-cancel-btn-fixed' },
      });

      if (result.isConfirmed) {
        try {
          await api.delete(`/accounting/suppliers/${row.id}`);
          this.rows = this.rows.filter(r => r.id !== row.id);
          await Swal.fire({ icon: 'success', title: 'Supplier deleted successfully', toast: true, position: 'top', timer: 1400, showConfirmButton: false });
        } catch (err) {
          await Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || err?.message || 'Failed to delete supplier.', toast: true, position: 'top', timer: 2200, showConfirmButton: false });
        }
      }
    },

    removeService(v) {
      this.form.services = (this.form.services || []).filter(x => x !== v);
    },

    toggleService(val, checked) {
      if (this.formMode === "view") return;
      const set = new Set(this.form.services || []);
      checked ? set.add(val) : set.delete(val);
      this.form.services = Array.from(set);
    },

    handleClickOutside(e) {
      const wrap = this.$refs.servicesWrapper;
      if (wrap && !wrap.contains(e.target)) this.showServicesDD = false;

      const cw = this.$refs.countryWrapper;
      if (cw && !cw.contains(e.target)) this.showCountryDD = false;

      const rw = this.$refs.repCountryWrapper;
      if (rw && !rw.contains(e.target)) this.showRepCountryDD = false;
    },
  },
};
</script>

<style scoped>
:deep(td) { @apply text-base; }
</style>

<style>
.swal2-confirm-btn-fixed {
  background-color: #ef4444 !important; color: white !important;
  padding: 10px 28px !important; margin: 10px !important;
  border-radius: 8px !important; font-weight: bold !important;
  display: inline-block !important; opacity: 1 !important; visibility: visible !important;
}
.swal2-cancel-btn-fixed {
  background-color: #6b7280 !important; color: white !important;
  padding: 10px 28px !important; margin: 10px !important;
  border-radius: 8px !important; font-weight: bold !important;
  display: inline-block !important; opacity: 1 !important; visibility: visible !important;
}
.swal2-actions { display: flex !important; opacity: 1 !important; visibility: visible !important; }
</style>
